import { useEffect, useState, type ChangeEvent } from 'react'
import { PLAN_PRICES, formatPrice, getStoredCurrency } from '../lib/currency'
import { subscribePaymentConfig, requestPlanActivation } from '../lib/db'
import { PAYMENT_CONFIG, whatsappContactUrl, isPaymentConfigured, normalizeUrl } from '../lib/payments'
import { toast } from '../lib/toast'
import type { PaymentGatewayConfig, PaymentMethod } from '../lib/types'

export default function PaymentOptions({
  uid,
  name,
  email,
  currentTier,
}: {
  uid: string
  name: string
  email: string
  currentTier?: string
}) {
  const currency = getStoredCurrency()
  const [selectedPlan, setSelectedPlan] = useState<'premium' | 'familia'>('premium')
  const [method, setMethod] = useState<PaymentMethod | null>(null)
  const [receipt, setReceipt] = useState<string | null>(null)
  const [amount, setAmount] = useState('')
  const [requested, setRequested] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [gatewayConfig, setGatewayConfig] = useState<PaymentGatewayConfig | null>(null)

  useEffect(() => {
    return subscribePaymentConfig(setGatewayConfig)
  }, [])

  const plans = [
    {
      id: 'premium' as const,
      icon: '⭐',
      name: 'Premium',
      price: formatPrice(PLAN_PRICES.premium[currency], currency),
      devices: 'Hasta 4 dispositivos',
      perks: ['Filtros web por edad', 'Bloqueo de apps', 'Reportes avanzados'],
    },
    {
      id: 'familia' as const,
      icon: '👨‍👩‍👦',
      name: 'Familia',
      price: formatPrice(PLAN_PRICES.familia[currency], currency),
      devices: 'Hasta 10 dispositivos',
      perks: ['Todo en Premium', 'Panel familiar completo', 'Soporte 24/7'],
    },
  ]

  const selected = plans.find((p) => p.id === selectedPlan)!
  // Links con monto exacto del plan elegido (per-plan) con fallback al genérico.
  const planLinks = {
    wompi:
      (selectedPlan === 'premium' ? gatewayConfig?.wompiPremiumUrl : gatewayConfig?.wompiFamiliaUrl) ||
      gatewayConfig?.wompiUrl ||
      PAYMENT_CONFIG.wompiCheckoutUrl,
    mercadoPago:
      (selectedPlan === 'premium' ? gatewayConfig?.mercadoPagoPremiumUrl : gatewayConfig?.mercadoPagoFamiliaUrl) ||
      gatewayConfig?.mercadoPagoUrl ||
      PAYMENT_CONFIG.mercadoPagoCheckoutUrl,
  }
  const linkOk = (u?: string) => !!u && !u.includes('TU_LINK')
  const hasGateways =
    isPaymentConfigured() || linkOk(planLinks.wompi) || linkOk(planLinks.mercadoPago)
  const hasBank = Boolean(gatewayConfig?.bankName && gatewayConfig?.accountNumber)
  const amountNum = parseInt((amount || '').replace(/[^0-9]/g, ''), 10) || 0

  const openPayment = (url: string, gateway: string) => {
    const finalUrl = normalizeUrl(url)
    if (!finalUrl || finalUrl.includes('TU_LINK')) {
      toast('El enlace de pago aún no está configurado.', 'error')
      return
    }
    window.open(finalUrl, '_blank')
    toast(`Abriendo ${gateway}…`, 'info')
  }

  const handleReceipt = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      toast('Imagen muy grande (máx 5MB)', 'error')
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const img = new window.Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const scale = Math.min(1, 800 / img.width)
        canvas.width = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height)
        setReceipt(canvas.toDataURL('image/jpeg', 0.7))
        toast('📷 Comprobante cargado', 'success')
      }
      img.src = reader.result as string
    }
    reader.readAsDataURL(file)
  }

  const requestActivation = async () => {
    setRequesting(true)
    try {
      await requestPlanActivation(uid, name, email, selectedPlan, method ?? 'transfer', receipt ?? undefined, amountNum || undefined)
      setRequested(true)
      toast('✅ Solicitud enviada. El administrador verificará tu pago.', 'success')
    } finally {
      setRequesting(false)
    }
  }

  const contactMsg = `Hola, soy ${name || email} y quiero activar el plan ${selected.name} de KidSafe (${selected.price}/mes).`

  return (
    <div className="space-y-4">
      {/* Selección de plan */}
      <div className="grid gap-3 sm:grid-cols-2">
        {plans.map((plan) => {
          const isCurrent = currentTier === plan.id
          const sel = selectedPlan === plan.id
          return (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`rounded-2xl border-2 p-4 text-left transition ${
                sel ? 'border-brand-600 bg-brand-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-800">
                  {plan.icon} {plan.name}
                </span>
                {isCurrent && (
                  <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                    ACTUAL
                  </span>
                )}
              </div>
              <p className="mt-1 text-2xl font-bold text-brand-600">
                {plan.price}
                <span className="text-xs font-normal text-slate-400">/mes</span>
              </p>
              <p className="mt-1 text-xs font-medium text-slate-500">{plan.devices}</p>
            </button>
          )
        })}
      </div>

      {/* Método de pago */}
      <div className="rounded-2xl bg-white p-5 ring-1 ring-slate-200">
        <h3 className="font-bold text-slate-800">Forma de pago</h3>
        <div className="mt-3 space-y-2">
          {/* Pasarelas: una opción por pasarela para saber dónde pagó */}
          {hasGateways && linkOk(planLinks.wompi) && (
            <button
              onClick={() => setMethod('wompi')}
              className={`flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition ${
                method === 'wompi' ? 'border-brand-600 bg-brand-50' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <span className="text-xl">💳</span>
              <div className="flex-1">
                <p className="font-medium text-slate-800">Wompi</p>
                <p className="text-xs text-slate-400">Tarjeta, PSE, Nequi · {selected.price}/mes</p>
              </div>
              <span
                className={`h-4 w-4 rounded-full border-2 ${
                  method === 'wompi' ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
                }`}
              />
            </button>
          )}
          {hasGateways && linkOk(planLinks.mercadoPago) && (
            <button
              onClick={() => setMethod('mercadopago')}
              className={`flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition ${
                method === 'mercadopago' ? 'border-brand-600 bg-brand-50' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <span className="text-xl">💙</span>
              <div className="flex-1">
                <p className="font-medium text-slate-800">MercadoPago</p>
                <p className="text-xs text-slate-400">Tarjeta y dinero en cuenta · {selected.price}/mes</p>
              </div>
              <span
                className={`h-4 w-4 rounded-full border-2 ${
                  method === 'mercadopago' ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
                }`}
              />
            </button>
          )}

          {/* Transferencia */}
          {hasBank && (
            <button
              onClick={() => setMethod('transfer')}
              className={`flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition ${
                method === 'transfer' ? 'border-brand-600 bg-brand-50' : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <span className="text-xl">🏦</span>
              <div className="flex-1">
                <p className="font-medium text-slate-800">Transferencia bancaria</p>
                <p className="text-xs text-slate-400">{gatewayConfig?.bankName} · {gatewayConfig?.accountType} {gatewayConfig?.accountNumber}</p>
              </div>
              <span
                className={`h-4 w-4 rounded-full border-2 ${
                  method === 'transfer' ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
                }`}
              />
            </button>
          )}
        </div>

        {/* Detalles transferencia */}
        {method === 'transfer' && gatewayConfig && (
          <div className="mt-3 rounded-xl bg-slate-50 p-4 text-sm">
            <p className="font-semibold text-slate-700">Datos para transferir:</p>
            <p className="mt-1 text-slate-600">
              🏦 {gatewayConfig.bankName} · {gatewayConfig.accountType}
            </p>
            <p className="text-slate-600">
              № {gatewayConfig.accountNumber} — {gatewayConfig.beneficiaryName}
            </p>
            {gatewayConfig.documentId && <p className="text-slate-400 text-xs">{gatewayConfig.documentId}</p>}
            <p className="mt-2 font-semibold text-brand-600">Valor a pagar: {selected.price}/mes</p>
            <p className="mt-1 text-xs text-slate-400">
              Después de transferir, sube el comprobante aquí abajo.
            </p>
          </div>
        )}

        {/* Botones pasarela: abren el link con el monto exacto del plan elegido */}
        {method === 'wompi' && (
          <div className="mt-3">
            <button
              onClick={() => openPayment(planLinks.wompi, 'Wompi')}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#2D2E83] py-3 font-semibold text-white hover:opacity-90"
            >
              💳 Pagar {selected.price} con Wompi
            </button>
          </div>
        )}
        {method === 'mercadopago' && (
          <div className="mt-3">
            <button
              onClick={() => openPayment(planLinks.mercadoPago, 'MercadoPago')}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#00B1EA] py-3 font-semibold text-white hover:opacity-90"
            >
              💙 Pagar {selected.price} con MercadoPago
            </button>
          </div>
        )}

        {/* Monto + comprobante OBLIGATORIOS en todos los métodos: es la prueba
            que el admin compara contra el precio del plan antes de activar. */}
        {method && (
          <div className="mt-3 space-y-3 rounded-xl bg-slate-50 p-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                ¿Cuánto pagaste? (valor exacto del comprobante)
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ej: 35000"
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-brand-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Comprobante (captura de la pasarela o transferencia)
              </label>
              <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white py-3 text-sm font-medium text-slate-600 hover:border-brand-400 hover:text-brand-600">
                {receipt ? '✅ Comprobante cargado (clic para cambiar)' : '📷 Subir comprobante de pago'}
                <input type="file" accept="image/*" onChange={handleReceipt} className="hidden" />
              </label>
              {receipt && (
                <img src={receipt} alt="Comprobante" className="mt-2 max-h-40 rounded-lg object-cover" />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Botón solicitar activación */}
      <button
        onClick={requestActivation}
        disabled={requesting || !method || !receipt || amountNum <= 0}
        className="w-full rounded-xl border-2 border-brand-600 py-3 text-sm font-semibold text-brand-600 hover:bg-brand-50 disabled:opacity-50"
      >
        {requesting ? 'Enviando…' : '✓ Ya realicé el pago — solicitar activación'}
      </button>

      {/* Contacto */}
      <div className="grid gap-2 sm:grid-cols-2">
        <a
          href={whatsappContactUrl(contactMsg, gatewayConfig?.whatsappNumber)}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
        >
          💬 WhatsApp
        </a>
        <a
          href={`mailto:${gatewayConfig?.adminEmail || PAYMENT_CONFIG.adminEmail}?subject=${encodeURIComponent('Activación KidSafe')}&body=${encodeURIComponent(contactMsg)}`}
          className="flex items-center justify-center gap-2 rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
        >
          📧 Email
        </a>
      </div>

      {requested && (
        <p className="rounded-xl bg-emerald-50 px-4 py-3 text-center text-sm text-emerald-700">
          ✅ Solicitud enviada. El administrador verificará tu pago y activará tu plan.
        </p>
      )}
    </div>
  )
}